/**
 * Screen Capture Hook - Web Version
 * Uses extension screenshot listener instead of Electron API
 */

import { useEffect, useRef } from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';
import { toast } from '../utils/toast';
import { useScreenshotListener } from './use-screenshot-listener';

const logger = createLogger('web:screen-capture');

type CaptureResolver = {
  resolve: (value: { success: boolean; dataUrl: string; timestamp: number }) => void;
  reject: (error: Error) => void;
  timeout: NodeJS.Timeout;
};

export function useScreenCapture() {
  const { screenshot, clearScreenshot } = useScreenshotListener();
  const pendingCaptureRef = useRef<CaptureResolver | null>(null);

  // Listen for capture errors
  useEffect(() => {
    const handleError = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.error) {
        logger.error('❌ [SCREEN-CAPTURE] Capture failed:', customEvent.detail.error);

        // Reject pending promise if any
        if (pendingCaptureRef.current) {
          clearTimeout(pendingCaptureRef.current.timeout);
          pendingCaptureRef.current.reject(new Error(customEvent.detail.error));
          pendingCaptureRef.current = null;
        }

        toast.error('Screenshot Failed', customEvent.detail.error);
      }
    };

    window.addEventListener('screenshot-capture-error', handleError);
    return () => window.removeEventListener('screenshot-capture-error', handleError);
  }, []);

  // Listen for incoming screenshots and resolve pending promises
  useEffect(() => {
    if (screenshot && pendingCaptureRef.current) {
      logger.info('✅ [SCREEN-CAPTURE] Screenshot received, resolving promise');
      clearTimeout(pendingCaptureRef.current.timeout);
      pendingCaptureRef.current.resolve({
        success: true,
        dataUrl: screenshot.dataUrl,
        timestamp: screenshot.timestamp,
      });
      pendingCaptureRef.current = null;
    }
  }, [screenshot]);

  const quickCapture = async (
    displayId?: string
  ): Promise<{ success: boolean; dataUrl: string; timestamp: number; error?: string }> => {
    logger.info('📸 [SCREEN-CAPTURE] Requesting screenshot from extension...');

    // Check if we're in iframe (extension sidebar)
    if (window.parent !== window) {
      // Clear any previous screenshot
      clearScreenshot();

      return new Promise((resolve, reject) => {
        // Set up timeout (10 seconds)
        const timeout = setTimeout(() => {
          logger.error('❌ [SCREEN-CAPTURE] Timeout waiting for screenshot');
          pendingCaptureRef.current = null;
          reject(new Error('Screenshot capture timed out'));
        }, 10000);

        // Store resolver for when screenshot arrives
        pendingCaptureRef.current = { resolve, reject, timeout };

        // Send request to extension
        logger.info('📤 [SCREEN-CAPTURE] Sending capture request to parent (extension)');
        window.parent.postMessage(
          {
            type: 'REQUEST_SCREEN_CAPTURE',
          },
          '*'
        );
      });
    } else {
      // In standalone web (no extension)
      logger.warn('⚠️ [SCREEN-CAPTURE] Not in extension iframe, cannot capture');
      return {
        success: false,
        dataUrl: '',
        timestamp: Date.now(),
        error: 'Screen capture requires the browser extension',
      };
    }
  };

  return {
    isAvailable: window.parent !== window, // Available only in extension iframe
    quickCapture,
  };
}

export function useScreenCapturePermissionGuard() {
  return {
    showPermissionModal: false,
    guardScreenCaptureAction: () => true, // Always allowed in web
    closePermissionModal: () => {},
  };
}
